import React, { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate, useSearchParams } from 'react-router-dom'

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { confirmPasswordReset } = useAuth()

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const token = searchParams.get('token')

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token')
    }
  }, [token])

  type Strength = 'empty' | 'bad' | 'good' | 'secure'
  const passwordStrength: Strength = useMemo(() => {
    const pw = password
    if (!pw) return 'empty'
    const hasUpper = /[A-Z]/.test(pw)
    const hasLower = /[a-z]/.test(pw)
    const hasDigit = /[0-9]/.test(pw)
    const hasSpecial = /[!@#$%^&*()_+\-={}\[\]\\|:;"'<>,.?/~]/.test(pw)
    const meetsBase = pw.length >= 10 && hasUpper && hasLower && hasDigit && hasSpecial
    if (!meetsBase) return 'bad'
    if (pw.length >= 14) return 'secure'
    return 'good'
  }, [password])

  const strengthLabel = passwordStrength === 'secure' ? 'Secure' : passwordStrength === 'good' ? 'Good' : passwordStrength === 'bad' ? 'Bad' : ''
  const strengthColor = passwordStrength === 'secure' ? 'text-green-600' : passwordStrength === 'good' ? 'text-yellow-600' : passwordStrength === 'bad' ? 'text-red-600' : 'text-neutral-400'
  const strengthBarClass = passwordStrength === 'secure' ? 'bg-green-500 w-full' : passwordStrength === 'good' ? 'bg-yellow-500 w-2/3' : passwordStrength === 'bad' ? 'bg-red-500 w-1/3' : 'bg-neutral-600 w-0'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!token) {
      setError('Invalid reset token')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (passwordStrength === 'bad') {
      setError('Password does not meet security requirements')
      return
    }

    setLoading(true)
    setError('')

    try {
      await confirmPasswordReset(token, password)
      setSuccess(true)
      setTimeout(() => {
        navigate('/')
      }, 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4 pt-32 pb-12">
        <div className="w-full max-w-md bg-[#141414] rounded-lg shadow-xl border border-white/10 p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Password Reset Successful</h2>
            <p className="text-neutral-400 mb-6">
              Your password has been successfully reset. Redirecting to home page...
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4 pt-32 pb-12">
      <div className="w-full max-w-md bg-[#141414] rounded-lg shadow-xl border border-white/10 p-8">
        <h2 className="text-2xl font-bold text-white mb-2">Reset Your Password</h2>
        <p className="text-sm text-neutral-400 mb-6">
          Enter your new password below.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-900/20 rounded-lg border border-red-500/30">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-neutral-300 mb-2">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={10}
                className="w-full px-4 py-3 pr-10 rounded-lg border border-white/20 bg-[#1a1a1a] text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-colors"
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-2 px-2 flex items-center text-neutral-400 hover:text-white"
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-5 0-9-4-9-7 0-1.06.39-2.16 1.125-3.175M6.18 6.18A9.967 9.967 0 0112 5c5 0 9 4 9 7 0 1.06-.39 2.16-1.125 3.175M3 3l18 18M9.88 9.88A3 3 0 0012 15a3 3 0 002.12-5.12"/></svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                )}
              </button>
            </div>
            {password && (
              <div className="mt-2">
                <div className="h-1.5 w-full bg-neutral-700 rounded">
                  <div className={`h-1.5 rounded transition-all ${strengthBarClass}`}></div>
                </div>
                <div className={`mt-1 text-xs ${strengthColor}`}>{strengthLabel || ' '}</div>
                {passwordStrength === 'bad' && (
                  <div className="mt-1 text-xs text-red-600">
                    Password must be at least 10 characters and include upper, lower, number, and special character.
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-neutral-300 mb-2">
              Confirm Password
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-lg border border-white/20 bg-[#1a1a1a] text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-colors"
              placeholder="Confirm new password"
            />
          </div>

          <button
            type="submit"
            disabled={loading || passwordStrength === 'bad' || !token}
            className="w-full py-3 px-4 bg-rose-600 hover:bg-rose-700 disabled:bg-rose-400 text-white font-medium rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-2"
            style={{ boxShadow: '0 2px 8px rgba(225, 29, 72, 0.25)' }}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Resetting...
              </div>
            ) : (
              'Reset Password'
            )}
          </button>
        </form>
      </div>
    </div>
  )
}

export default ResetPassword
